import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Download, Gift, ArrowLeft, Apple, Play, Gamepad2, NotebookPen, Ghost, Zap, Timer, Lock, Smartphone } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { api } from "../lib/api";
import { useLang } from "../i18n";

const ICONS = { gamepad: Gamepad2, notebook: NotebookPen, ghost: Ghost, zap: Zap, timer: Timer, lock: Lock };

export default function AppDetail() {
  const { appId } = useParams();
  const { t, lang } = useLang();
  const [app, setApp] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/apps/${appId}`).then(({ data }) => setApp(data)).catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [appId]);

  if (error) return (
    <div className="min-h-screen bg-[#080A0C] terminal-grid">
      <Navbar />
      <div className="pt-40 text-center font-mono text-gray-500" data-testid="app-not-found">{t.notFound}</div>
    </div>
  );
  if (!app) return <div className="min-h-screen bg-[#080A0C]" />;

  const Icon = ICONS[app.icon] || Smartphone;
  const desc = lang === "it" ? app.desc_it : app.desc_en;
  const tagline = lang === "it" ? app.tagline : (app.tagline_en || app.tagline);

  return (
    <div className="min-h-screen bg-[#080A0C] text-gray-100 terminal-grid">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-24">
        <Link to="/#apps" data-testid="app-detail-back-link" className="inline-flex items-center gap-2 font-mono text-xs text-gray-400 hover:text-[#00FF66] transition-colors">
          <ArrowLeft size={13} /> {t.back}
        </Link>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 mt-10 items-start">
          {/* Screenshot */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex justify-center">
            <div className="absolute inset-0 blur-[100px] rounded-full scale-90" style={{ background: `${app.color}22` }} />
            <img src={app.image} alt={`${app.name} screenshot`} data-testid="app-detail-screenshot"
              className="relative rounded-3xl border border-[#1F2937] max-h-[560px] w-auto object-contain shadow-2xl" />
          </motion.div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center border shrink-0" style={{ borderColor: `${app.color}55`, background: `${app.color}10` }}>
                <Icon size={30} style={{ color: app.color }} />
              </div>
              <div>
                <h1 data-testid="app-detail-name" className="font-display font-bold text-3xl sm:text-4xl text-white">{app.name}</h1>
                <p className="text-gray-400 mt-1">{tagline}</p>
              </div>
            </div>

            <span className="inline-block mt-5 font-mono text-[10px] tracking-[0.2em] text-[#00FF66] border border-[#00FF66]/30 rounded-full px-4 py-1.5 bg-[#00FF66]/5">{t.freeBadge}</span>

            <div className="grid grid-cols-3 gap-4 mt-8">
              {[
                { icon: Star, val: app.rating, label: t.ratingLbl, cls: "text-[#F59E0B]" },
                { icon: Download, val: app.downloads.toLocaleString(lang === "it" ? "it-IT" : "en-GB"), label: t.downloadsLbl, cls: "text-gray-300" },
                { icon: Gift, val: `€${app.donations.toLocaleString(lang === "it" ? "it-IT" : "en-GB")}`, label: t.donatedLbl, cls: "text-[#00FF66]" },
              ].map((s) => (
                <div key={s.label} className="glass-card rounded-xl p-4 text-center">
                  <s.icon size={16} className={`mx-auto mb-2 ${s.cls}`} />
                  <div className="font-display font-bold text-white text-lg">{s.val}</div>
                  <div className="font-mono text-[9px] tracking-widest text-gray-500 uppercase mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <h2 className="font-display font-semibold text-lg text-white mt-9 mb-3">{t.about}</h2>
            <p data-testid="app-detail-description" className="text-gray-400 leading-relaxed">{desc}</p>

            <div className="flex flex-wrap gap-2 mt-6">
              {app.platforms.map((p) => (
                <span key={p} className="font-mono text-[10px] tracking-wider px-3 py-1.5 rounded-full border border-[#1F2937] text-gray-400">{p}</span>
              ))}
              <span className="font-mono text-[10px] tracking-wider px-3 py-1.5 rounded-full border border-[#1F2937] text-gray-500">{t.versionLbl} {app.version}</span>
            </div>

            <div className="flex flex-wrap gap-4 mt-9">
              {app.store_ios && (
                <a href={app.store_ios} target="_blank" rel="noopener noreferrer" data-testid="app-detail-ios-btn"
                  className="btn-neon px-7 py-3.5 text-xs inline-flex items-center gap-2">
                  <Apple size={15} /> {t.getIos}
                </a>
              )}
              {app.store_android && (
                <a href={app.store_android} target="_blank" rel="noopener noreferrer" data-testid="app-detail-android-btn"
                  className="btn-ghost-outline px-7 py-3.5 text-xs inline-flex items-center gap-2">
                  <Play size={14} /> {t.getAndroid}
                </a>
              )}
            </div>

            <p className="flex items-center gap-2 mt-8 text-sm text-gray-500 border border-[#1F2937] rounded-xl px-5 py-4 bg-[#0D1117]/60">
              <Gift size={15} className="text-[#00FF66] shrink-0" /> {t.donateNote}
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
